/**
 * Users — Single Sign-On.
 *
 * Foundations 2 (Users rail), B2d-T4.
 *
 * ─── WHAT THIS PAGE USED TO BE ────────────────────────────────────────────
 *
 * Two cards of static copy telling the tenant admin to "contact your platform
 * administrator", with `No API calls.` in its own header comment. decisions.md
 * 2026-09-08 calls tenant self-service SSO *"a required Users-rail feature, to
 * be built properly"*; this is it. The route, the layout and the card shape are
 * the ones that were already here — the copy becomes a provider list.
 *
 * ─── WHAT IT SHOWS, AND WHY ───────────────────────────────────────────────
 *
 * THE LAST TEST RESULT, PLAINLY (decisions.md 2026-09-13 #16). Three states,
 * rendered differently on purpose: never tested, last test passed, last test
 * FAILED — and a failed connection is saved, so the row has to say so rather
 * than looking like every other row. A connection that has never succeeded
 * must not look healthy.
 *
 * THE THREE URLS AN ADMIN HAS TO COPY — sign-in, redirect/ACS, and (for SAML)
 * the entity id we present. They are computed by the server and displayed, not
 * typed in, because an admin who guesses one of them gets a failure the product
 * cannot explain.
 *
 * WHAT IT NEVER SHOWS is the client secret. The response shape has no field for
 * one; the form's secret box says whether a secret is stored and leaves the
 * stored value alone when submitted empty. Removing one is its own, stated
 * action — the store's documented clearing branch, which was unreachable from
 * here until B2d-T4's review found that deleting the connection (and every
 * identity linked to it) was the only way out.
 *
 * NO CONTROL OFFERS AN ACTION THE SERVER REFUSES. Every mutation costs
 * `write:sso` at the gate, so every write control is behind `useHasPermission`
 * — this was the only Users-rail screen without that check — and the JIT
 * default-role picker reads the roles STORE, filtered to the keys the server
 * accepts and to the roles the caller could actually assign.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ShieldCheck,
  Plus,
  Trash2,
  Pencil,
  Copy,
  CheckCircle2,
  XCircle,
  CircleDashed,
  Loader2,
  PlugZap,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  useSsoConnections,
  useCreateSsoConnection,
  useUpdateSsoConnection,
  useDeleteSsoConnection,
  useTestSsoConnection,
  type SsoConnection,
  type SsoConnectionWrite,
} from '@/lib/use-sso-connections';
import { useDepartments } from '@/lib/use-departments';
import { useRoles } from '@/lib/use-roles';
import { useHasPermission } from '@/lib/use-has-permission';
import { useAuth } from '@/contexts/auth-context';
import { PLATFORM_ROLE_KEYS } from '@argusai/shared/schemas/roles';
import type { RbacPermission } from '@argusai/shared/schemas/permissions';

export const Route = createFileRoute('/users/sso')({
  component: SsoTab,
});

/**
 * THE JIT DEFAULT-ROLE PICKER READS THE ROLES STORE.
 *
 * It used to be a literal `[{key:'viewer'},{key:'member'},{key:'power_user'},
 * {key:'admin'}]` in this file — a catalogue of a noun, hardcoded in the
 * browser, against principle 3 ("nothing that describes a noun is hardcoded")
 * and against every other role picker on this rail, all of which read the store
 * through `useRoles()` (assign-role-dialog, create-user-dialog,
 * groups.$groupId, roles.tsx). It had already drifted: the shipped catalogue is
 * five keys, that list was four, so `owner` was a value the API would store and
 * this screen could not express — and a role's display name came from this file
 * rather than `roles.name`, so the same key could read "Power user" here and
 * whatever the tenant calls it two tabs away.
 *
 * WHAT IT STILL FILTERS, and why that is not a second hardcoded list: the
 * server accepts only `PLATFORM_ROLE_KEYS` for this field (`users-sso.ts` →
 * `isPlatformRoleKey`, and `sso-service.ts` refuses the same at sign-in
 * because `writeMembershipRole` assigns the platform row for a key). Offering a
 * tenant-defined role here would be a control that offers an action the server
 * refuses — the exact thing this batch's acceptance forbids. The filter reads
 * the shared constant, so if that set ever grows this picker grows with it.
 */
function useJitRoleOptions(): { options: Array<{ key: string; label: string }>; isLoading: boolean } {
  const { roles, isLoading } = useRoles();
  const { can } = useAuth();

  const options = roles
    .filter((r) => (PLATFORM_ROLE_KEYS as readonly string[]).includes(r.key))
    .filter((r) => r.retiredAt === null)
    // AND ONLY THE ONES THE CALLER COULD ACTUALLY ASSIGN. `users-sso.ts`
    // applies the same self-escalation guard the other three role-assigning
    // doors apply — `canHoldAll(principal, resolveRoleGrantsByKey(key))` — and
    // answers 403 otherwise. A picker offering `owner` to an Admin would be a
    // control whose only outcome is a refusal after the form is filled in.
    .filter((r) =>
      r.permissions.every((g) => can(`${g.action}:${g.resource}` as RbacPermission)),
    )
    .map((r) => ({ key: r.key, label: r.name }));

  return { options, isLoading };
}

function CopyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 truncate rounded bg-muted px-2 py-1 text-[11px]">
          {value || '—'}
        </code>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2"
          title={`Copy ${label}`}
          onClick={() => {
            void navigator.clipboard?.writeText(value);
            toast.success(`${label} copied`);
          }}
        >
          <Copy className="h-3 w-3" />
        </Button>
      </div>
    </div>
  );
}

/**
 * The last test result. NEVER TESTED IS ITS OWN STATE — rendering it as a blank
 * or as a neutral row is what lets a connection that has never worked look like
 * one that does.
 */
function LastTestBadge({ connection }: { connection: SsoConnection }) {
  if (!connection.lastTest) {
    return (
      <Badge
        variant="outline"
        className="text-[10px] gap-1 text-muted-foreground"
        title="This connection has never been tested."
      >
        <CircleDashed className="h-2.5 w-2.5" />
        Never tested
      </Badge>
    );
  }
  const when = new Date(connection.lastTest.at).toLocaleString();
  if (connection.lastTest.outcome === 'success') {
    return (
      <Badge
        variant="outline"
        className="text-[10px] gap-1 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
        title={`${when} — ${connection.lastTest.message ?? ''}`}
      >
        <CheckCircle2 className="h-2.5 w-2.5" />
        Last test passed
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="text-[10px] gap-1 bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30"
      title={`${when} — ${connection.lastTest.message ?? ''}`}
    >
      <XCircle className="h-2.5 w-2.5" />
      Last test failed
    </Badge>
  );
}

function SsoTab() {
  const { connections, isLoading, error } = useSsoConnections();
  const testConnection = useTestSsoConnection();
  const removeConnection = useDeleteSsoConnection();
  const [editing, setEditing] = useState<SsoConnection | null>(null);
  const [adding, setAdding] = useState(false);
  // NO CONTROL OFFERS AN ACTION THE SERVER REFUSES. `read:sso` gets the list;
  // every mutation on this screen — add, edit, remove and test — costs
  // `write:sso` at the gate (`routes/v1/users-sso.ts`, one `writeGate` on all
  // four). Without this the page was the only Users-rail screen with no
  // permission check at all: routes/users/index.tsx, departments.tsx,
  // groups.tsx and $userId.tsx all read `useHasPermission`. A role granted
  // read but not write could fill in an entire OIDC connection — issuer,
  // client id, secret, role, departments — and lose the lot to a 403.
  const canWrite = useHasPermission('write', 'sso');

  return (
    <div className="flex flex-col h-full p-6 space-y-4 overflow-y-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Single Sign-On</h1>
          <p className="text-xs text-muted-foreground mt-1">
            Configure how your users authenticate with external identity
            providers.
          </p>
        </div>
        {canWrite && (
          <Button size="sm" className="h-8 text-xs gap-1" onClick={() => setAdding(true)}>
            <Plus className="h-3.5 w-3.5" />
            Add connection
          </Button>
        )}
      </div>

      {error ? (
        <Card>
          <CardContent className="p-6 text-xs text-red-600 dark:text-red-400">
            Single sign-on connections could not be loaded. Nothing is shown
            rather than an empty list that would read as &quot;no connections
            configured&quot;.
          </CardContent>
        </Card>
      ) : isLoading ? (
        <Card>
          <CardContent className="p-6 flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Loading connections…
          </CardContent>
        </Card>
      ) : connections.length === 0 ? (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary/10">
                <ShieldCheck className="h-5 w-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <h2 className="text-sm font-semibold">
                  No single sign-on connections yet
                </h2>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Add an OpenID Connect or SAML 2.0 provider and your users can
                  sign in with it. People arriving for the first time are given
                  an account with the role and departments you choose on the
                  connection.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        connections.map((connection) => (
          <Card key={connection.id}>
            <CardContent className="p-6 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-sm font-semibold truncate">
                      {connection.name}
                    </h2>
                    <Badge variant="outline" className="text-[10px] uppercase">
                      {connection.protocol}
                    </Badge>
                    {connection.isEnabled ? (
                      <Badge
                        variant="outline"
                        className="text-[10px] bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                      >
                        Enabled
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">
                        Disabled
                      </Badge>
                    )}
                    <LastTestBadge connection={connection} />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    New people get{' '}
                    <span className="font-medium">{connection.defaultRoleKey}</span>
                    {connection.defaultDepartmentScope.all
                      ? ', all departments'
                      : `, ${connection.defaultDepartmentScope.ids.length} department(s)`}
                    {connection.jitEnabled
                      ? '.'
                      : ' — but new accounts are NOT created by this connection.'}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {canWrite && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs gap-1"
                    disabled={testConnection.isPending}
                    onClick={async () => {
                      try {
                        const result = await testConnection.mutateAsync(connection.id);
                        // A failure is a recorded outcome, not an error: say
                        // what happened either way and leave the row saved.
                        if (result.outcome === 'success') {
                          toast.success(result.message ?? 'Test connection passed');
                        } else {
                          toast.error(result.message ?? 'Test connection failed');
                        }
                      } catch (err) {
                        toast.error(
                          err instanceof Error
                            ? err.message
                            : 'The test could not be run',
                        );
                      }
                    }}
                  >
                    {testConnection.isPending ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <PlugZap className="h-3 w-3" />
                    )}
                    Test
                  </Button>
                  )}
                  {canWrite && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2"
                    title="Edit"
                    onClick={() => setEditing(connection)}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  )}
                  {canWrite && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-red-600 dark:text-red-400"
                    title="Remove"
                    onClick={async () => {
                      if (
                        !window.confirm(
                          `Remove "${connection.name}"? Everyone who signs in through it loses that route into this workspace.`,
                        )
                      ) {
                        return;
                      }
                      try {
                        const res = await removeConnection.mutateAsync(connection.id);
                        toast.success(
                          res.unlinkedIdentities > 0
                            ? `Connection removed; ${res.unlinkedIdentities} linked account(s) can no longer sign in with it.`
                            : 'Connection removed.',
                        );
                      } catch (err) {
                        toast.error(
                          err instanceof Error ? err.message : 'Could not remove it',
                        );
                      }
                    }}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                  )}
                </div>
              </div>

              {connection.lastTest?.outcome === 'failure' && (
                <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-[11px] text-red-700 dark:text-red-300">
                  <span className="font-medium">
                    Last test ({new Date(connection.lastTest.at).toLocaleString()}):
                  </span>{' '}
                  {connection.lastTest.message ?? 'no detail recorded'}
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <CopyField label="Sign-in URL" value={connection.signInUrl} />
                <CopyField
                  label={
                    connection.protocol === 'saml'
                      ? 'ACS / Reply URL'
                      : 'Redirect URI'
                  }
                  value={connection.redirectUri}
                />
                {connection.protocol === 'saml' ? (
                  <CopyField
                    label="IdP single sign-on URL"
                    value={connection.saml?.ssoUrl ?? ''}
                  />
                ) : (
                  <CopyField label="Issuer" value={connection.oidc?.issuer ?? ''} />
                )}
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    Client secret
                  </div>
                  <div className="text-[11px] text-muted-foreground py-1">
                    {connection.protocol === 'saml'
                      ? 'Not used by SAML.'
                      : connection.hasClientSecret
                        ? 'Stored, encrypted. It cannot be read back.'
                        : 'Not configured.'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))
      )}

      {/* `canWrite` again, not only on the buttons: the gate is the condition,
          not the click path that reached it. */}
      {canWrite && (adding || editing) && (
        <ConnectionDialog
          connection={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

/**
 * ONE dialog for add and edit. The difference is the row it starts from and
 * the mutation it ends with, which is not enough difference to justify two
 * components that drift.
 */
function ConnectionDialog({
  connection,
  onClose,
}: {
  connection: SsoConnection | null;
  onClose: () => void;
}) {
  const create = useCreateSsoConnection();
  const update = useUpdateSsoConnection();
  const { departments } = useDepartments({ pageSize: 200 });
  const { options: roleOptions, isLoading: rolesLoading } = useJitRoleOptions();

  const [protocol, setProtocol] = useState<'oidc' | 'saml'>(
    connection?.protocol === 'saml' ? 'saml' : 'oidc',
  );
  const [name, setName] = useState(connection?.name ?? '');
  const [isEnabled, setIsEnabled] = useState(connection?.isEnabled ?? true);
  const [jitEnabled, setJitEnabled] = useState(connection?.jitEnabled ?? true);
  const [roleKey, setRoleKey] = useState(connection?.defaultRoleKey ?? 'member');
  const [allDepartments, setAllDepartments] = useState(
    connection?.defaultDepartmentScope.all ?? true,
  );
  const [departmentIds, setDepartmentIds] = useState<string[]>(
    connection?.defaultDepartmentScope.ids ?? [],
  );

  const [issuer, setIssuer] = useState(connection?.oidc?.issuer ?? '');
  const [clientId, setClientId] = useState(connection?.oidc?.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  /**
   * THE THIRD STATE, AND THE ONLY WAY TO REMOVE A CREDENTIAL.
   *
   * `sso-connection.repository.ts:315-319` states the contract — omitted keeps
   * the stored ciphertext, `''` CLEARS it — and `updateSsoConnection`
   * implements it. But the form only put `clientSecret` in the body when the
   * box was non-empty, so the clearing branch was unreachable from the only
   * surface the noun has: an admin who moved a provider to a public client, or
   * pasted a secret into the wrong connection, could remove it only by DELETING
   * the connection, which cascades every `user_identities` row linked to it and
   * un-links everyone who ever signed in through it.
   *
   * Blanking the box cannot mean "clear" — that is what "leave blank to keep
   * it" already means, and guessing between the two is how a working connection
   * loses its credential on an unrelated edit. So the intent is stated.
   */
  const [clearSecret, setClearSecret] = useState(false);
  const [scopes, setScopes] = useState(
    (connection?.oidc?.scopes ?? []).join(' '),
  );

  const [entityId, setEntityId] = useState(connection?.saml?.entityId ?? '');
  const [ssoUrl, setSsoUrl] = useState(connection?.saml?.ssoUrl ?? '');
  const [certificate, setCertificate] = useState(
    connection?.saml?.certificate ?? '',
  );
  const [metadataUrl, setMetadataUrl] = useState(
    connection?.saml?.metadataUrl ?? '',
  );

  const pending = create.isPending || update.isPending;

  function buildBody(): SsoConnectionWrite {
    const common = {
      name: name.trim(),
      isEnabled,
      jitEnabled,
      defaultRoleKey: roleKey,
      defaultDepartmentScope: allDepartments
        ? { all: true, ids: [] }
        : { all: false, ids: departmentIds },
    };
    if (protocol === 'saml') {
      return {
        ...common,
        protocol: 'saml',
        saml: {
          entityId: entityId.trim(),
          ssoUrl: ssoUrl.trim(),
          certificate: certificate.trim(),
          metadataUrl: metadataUrl.trim() === '' ? null : metadataUrl.trim(),
        },
      } as SsoConnectionWrite;
    }
    return {
      ...common,
      protocol: 'oidc',
      oidc: {
        issuer: issuer.trim(),
        clientId: clientId.trim(),
        scopes: scopes.split(/\s+/).filter((s) => s.length > 0),
      },
      // OMITTED when untouched, so the stored ciphertext is kept. Sent as ''
      // ONLY when the admin asked for it to be removed, which is the store's
      // documented clearing branch and now reachable from the screen.
      ...(clearSecret
        ? { clientSecret: '' }
        : clientSecret.length > 0
          ? { clientSecret }
          : {}),
    } as SsoConnectionWrite;
  }

  async function submit() {
    try {
      const body = buildBody();
      if (connection) {
        await update.mutateAsync({ id: connection.id, body });
        toast.success('Connection saved. Run the test to confirm it works.');
      } else {
        await create.mutateAsync(body);
        toast.success('Connection added. Run the test to confirm it works.');
      }
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save');
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sm">
            {connection ? 'Edit connection' : 'Add SSO connection'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Name</Label>
            <Input
              className="h-8 text-xs"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Okta, Entra ID, Ping…"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Protocol</Label>
            <Select
              value={protocol}
              onValueChange={(v) => setProtocol(v as 'oidc' | 'saml')}
              disabled={Boolean(connection)}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="oidc">OpenID Connect</SelectItem>
                <SelectItem value="saml">SAML 2.0</SelectItem>
              </SelectContent>
            </Select>
            {connection && (
              <p className="text-[10px] text-muted-foreground">
                The protocol cannot be changed on an existing connection —
                remove it and add a new one.
              </p>
            )}
          </div>

          {protocol === 'oidc' ? (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">Issuer</Label>
                <Input
                  className="h-8 text-xs"
                  value={issuer}
                  onChange={(e) => setIssuer(e.target.value)}
                  placeholder="https://example.okta.com/oauth2/default"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Client ID</Label>
                <Input
                  className="h-8 text-xs"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Client secret</Label>
                <Input
                  type="password"
                  className="h-8 text-xs"
                  value={clientSecret}
                  disabled={clearSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder={
                    connection?.hasClientSecret
                      ? 'Stored — leave blank to keep it'
                      : 'Paste the secret from your provider'
                  }
                />
                <p className="text-[10px] text-muted-foreground">
                  Encrypted at rest and never shown again. Leave blank to keep
                  the stored one.
                </p>
                {connection?.hasClientSecret && (
                  <label className="flex items-center gap-2 text-[11px] cursor-pointer">
                    <input
                      type="checkbox"
                      checked={clearSecret}
                      onChange={(e) => {
                        setClearSecret(e.target.checked);
                        if (e.target.checked) setClientSecret('');
                      }}
                    />
                    Remove the stored client secret (the provider becomes a
                    public client)
                  </label>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Extra scopes (optional)</Label>
                <Input
                  className="h-8 text-xs"
                  value={scopes}
                  onChange={(e) => setScopes(e.target.value)}
                  placeholder="groups offline_access"
                />
                <p className="text-[10px] text-muted-foreground">
                  openid, email and profile are always requested.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs">IdP entity ID</Label>
                <Input
                  className="h-8 text-xs"
                  value={entityId}
                  onChange={(e) => setEntityId(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Single sign-on URL</Label>
                <Input
                  className="h-8 text-xs"
                  value={ssoUrl}
                  onChange={(e) => setSsoUrl(e.target.value)}
                  placeholder="https://idp.example.com/sso/saml"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Signing certificate</Label>
                <Textarea
                  className="text-[11px] font-mono min-h-[100px]"
                  value={certificate}
                  onChange={(e) => setCertificate(e.target.value)}
                  placeholder="-----BEGIN CERTIFICATE-----"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Metadata URL (optional)</Label>
                <Input
                  className="h-8 text-xs"
                  value={metadataUrl}
                  onChange={(e) => setMetadataUrl(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="flex items-center justify-between gap-3">
            <div>
              <Label className="text-xs">Enabled</Label>
              <p className="text-[10px] text-muted-foreground">
                A disabled connection refuses every sign-in.
              </p>
            </div>
            <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              <Label className="text-xs">Create accounts on first sign-in</Label>
              <p className="text-[10px] text-muted-foreground">
                Off means only people who already have an account here can use it.
              </p>
            </div>
            <Switch checked={jitEnabled} onCheckedChange={setJitEnabled} />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Role for new accounts</Label>
            <Select value={roleKey} onValueChange={setRoleKey} disabled={rolesLoading}>
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder={rolesLoading ? 'Loading roles…' : 'Choose a role'} />
              </SelectTrigger>
              <SelectContent>
                {roleOptions.map((r) => (
                  <SelectItem key={r.key} value={r.key}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!rolesLoading && roleOptions.length === 0 && (
              <p className="text-[10px] text-red-600 dark:text-red-400">
                You do not hold the permissions of any role this connection
                could assign, so it cannot create accounts.
              </p>
            )}
          </div>

          <div className="flex items-center justify-between gap-3">
            <div>
              <Label className="text-xs">All departments</Label>
              <p className="text-[10px] text-muted-foreground">
                Off lets you pick the departments new accounts are scoped to.
              </p>
            </div>
            <Switch
              checked={allDepartments}
              onCheckedChange={(next) => {
                setAllDepartments(next);
                if (next) setDepartmentIds([]);
              }}
            />
          </div>

          {!allDepartments && (
            <div className="space-y-1.5">
              <Label className="text-xs">Departments</Label>
              <div className="max-h-40 overflow-y-auto rounded border p-2 space-y-1">
                {departments.length === 0 ? (
                  <p className="text-[10px] text-muted-foreground">
                    No departments exist in this workspace yet.
                  </p>
                ) : (
                  departments.map((d) => (
                    <label
                      key={d.id}
                      className="flex items-center gap-2 text-xs cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={departmentIds.includes(d.id)}
                        onChange={(e) =>
                          setDepartmentIds((prev) =>
                            e.target.checked
                              ? [...prev, d.id]
                              : prev.filter((id) => id !== d.id),
                          )
                        }
                      />
                      {d.name}
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button size="sm" className="h-8 text-xs" onClick={submit} disabled={pending}>
            {pending ? 'Saving…' : connection ? 'Save' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
